/**
 * PaymentStatusBadge - v1.0
 * Reusable component for displaying payment status with consistent styling
 *
 * Usage:
 * <PaymentStatusBadge status="processing" />
 * <PaymentStatusBadge status="accepted" size="large" />
 *
 * Status values:
 * - pending     - Waiting for user action
 * - processing  - Payment detected, confirming
 * - accepted    - Payment complete
 * - rejected    - Payment failed
 * - expired     - Payment timed out
 * - partial     - Underpaid
 * - refund_pending - Refund requested
 * - refunded    - Refund completed
 */

import React from 'react';
import { Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningIcon from '@mui/icons-material/Warning';
import ReplayIcon from '@mui/icons-material/Replay';
import { getStatusDisplayInfo } from '../../api/paymentApi';

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS ICONS
// ═══════════════════════════════════════════════════════════════════════════════

const getStatusIcon = (status) => {
    const normalizedStatus = status?.toLowerCase() || 'pending';

    switch (normalizedStatus) {
        case 'accepted':
            return <CheckCircleIcon sx={{ fontSize: 16 }} />;
        case 'rejected':
        case 'expired':
            return <ErrorIcon sx={{ fontSize: 16 }} />;
        case 'processing':
            return <AutorenewIcon sx={{ fontSize: 16 }} />;
        case 'partial':
            return <WarningIcon sx={{ fontSize: 16 }} />;
        case 'refund_pending':
        case 'refunded':
            return <ReplayIcon sx={{ fontSize: 16 }} />;
        case 'pending':
        default:
            return <AccessTimeIcon sx={{ fontSize: 16 }} />;
    }
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const PaymentStatusBadge = ({
    status,
    size = 'medium', // 'small' | 'medium' | 'large'
    showIcon = true
}) => {
    const displayInfo = getStatusDisplayInfo(status);

    // Size configurations
    const sizeConfig = {
        small: {
            height: 24,
            fontSize: '0.7rem',
            px: 1
        },
        medium: {
            height: 28,
            fontSize: '0.8rem',
            px: 1.5
        },
        large: {
            height: 32,
            fontSize: '0.875rem',
            px: 2
        }
    };

    const config = sizeConfig[size] || sizeConfig.medium;

    return (
        <Chip
            icon={showIcon ? getStatusIcon(status) : undefined}
            label={displayInfo.label}
            size={size === 'small' ? 'small' : 'medium'}
            sx={{
                backgroundColor: displayInfo.bgColor,
                color: displayInfo.color,
                fontWeight: 600,
                fontSize: config.fontSize,
                height: config.height,
                px: config.px,
                border: `1px solid ${displayInfo.color}20`,
                '& .MuiChip-icon': {
                    color: displayInfo.color
                },
                '& .MuiChip-label': {
                    px: showIcon ? 0.5 : 1
                }
            }}
        />
    );
};

export default PaymentStatusBadge;
